import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { LabelListComponent } from '../label-list.component';
import { StatusBadgeComponent } from '../status-badge.component';
import { STATUS_DOT_CLASSES } from '../status-dot';
import { UploadDropzoneComponent } from '../upload/upload-dropzone.component';
import { TippyTooltipDirective } from '../../directives/tippy-tooltip.directive';
import { isDeviatingVariant } from 'app/shared/utils/status-badge-resolver.util';
import {
  documentBadgeIcon,
  documentBadgeLabel,
  documentBadgeTone,
  type DocumentCardBadge,
} from 'app/shared/utils/document-card-status';
import type {
  DocumentCardFile,
  DocumentCardMenu,
  DocumentCardMetaRow,
  DocumentCardPrimaryAction,
  DocumentCardUploadMode,
  DocumentCardVariant,
} from './document-card.types';

const PRIMARY_ACTION_LABELS: Record<Exclude<DocumentCardPrimaryAction, null>, string> = {
  upload: 'Uploaden',
  replace: 'Vervangen',
  retry: 'Opnieuw uploaden',
  complete: 'Gegevens aanvullen',
};

/** Reuses the card's existing selectors so e2e specs keep working across both variants. */
const PRIMARY_ACTION_DATA_CY: Record<Exclude<DocumentCardPrimaryAction, null>, string> = {
  upload: 'document-upload-button',
  replace: 'document-replace-button',
  retry: 'document-replace-button',
  complete: 'document-complete-button',
};

/**
 * The one document card (#20856). Extracted verbatim from the `documents-cards` entity template,
 * which is the leading design, so that the required-documents checklist and readonly-onboarding
 * render the same card instead of three hand-maintained copies of it.
 *
 * Presentational and DI-free: it derives nothing but its own labels (badge wording, expiry line,
 * short dates) from the shared `document-card-status` helpers, and emits an intent per action.
 * Data loading, permissions and the write path stay with the hosting surface.
 */
@Component({
  selector: 'app-document-card',
  imports: [
    NgTemplateOutlet,
    MatIconModule,
    MatMenuModule,
    MatProgressSpinner,
    LabelListComponent,
    StatusBadgeComponent,
    UploadDropzoneComponent,
    TippyTooltipDirective,
  ],
  template: `
    <!--
      Row variant (Figma 3802:165013, component "Item list" Type=Detailed/Horizontal).
      Deliberately thinner than the card: no ...-menu, no file block, no rejection reason — the
      onboarding design routes all of those actions through the upload modal. The notice is the
      exception: it is the only place the carrier reads what is wrong with THIS document. The
      row rhythm itself (padding-top, hairline, spacing between rows) belongs to the LIST that
      renders these rows, not here — only the list knows which row is last and therefore gets no
      divider.
    -->
    @if (variant() === 'row') {
      <!--
        Kolom om de regel heen: de notice hoort onder de HELE regel, niet naast de knop. Zonder
        notice rendert de ng-container niets, dus de gap valt weg en de regel staat er pixelgelijk
        mee. Het data-cy-attribuut staat op deze buitenste div zodat de notice bij zijn eigen rij
        hoort voor wie de rij als geheel adresseert.
      -->
      <div class="flex flex-col gap-2" [attr.data-cy]="dataCy()">
        <div class="flex items-start gap-3">
          <div class="flex min-w-0 flex-1 flex-col gap-1">
            <div class="flex flex-wrap items-center gap-2">
              <!-- text-base = 0.875rem = 14px in deze repo's hertekende schaal (tailwind.config.js:72);
                   Figma zet titel en hint allebei op Inter Medium 14px / lh 1.4. -->
              <span class="text-base font-medium leading-[1.4] text-default">
                {{ title() }}
                @if (required()) {
                  <span aria-hidden="true"> *</span><span class="sr-only"> (verplicht)</span>
                }
              </span>
              @if (showBadge()) {
                <app-status-badge
                  [label]="badgeLabel()"
                  [variant]="badgeVariant()"
                  [icon]="badgeIcon()"></app-status-badge>
              }
              <!--
                Geen "Optioneel"-markering in deze variant: het ontwerp draagt verplicht/optioneel
                uitsluitend via de asterisk achter de titel (rij 4 en 5 van 3802:165013 hebben er
                geen). De sr-only "(verplicht)" hierboven houdt dat ook voor schermlezers leesbaar.
              -->
            </div>
            @if (description()) {
              <p class="m-0 text-base font-medium leading-[1.4] text-[var(--dd-card-muted-fg)]">{{ description() }}</p>
            }
          </div>
          @if (primaryAction(); as action) {
            <!--
              Figma "Type=Secondary, Size=Small": 32px hoog, 12px zijpadding, radius 8, label 14px
              Medium. h-8/px-3/rounded-lg/text-base komen daar exact op uit. De kleur MOET van
              --dd-btn-secondary-* komen en niet van --dd-badge-neutral-* (wat de kaartvariant
              hierboven gebruikt): die twee zijn in light identiek (#EEEFF1) maar lopen in dark uit
              elkaar — #545559 (Fill/Secondary/Default) tegenover #2C2C2E (background/subdued).
            -->
            <button
              type="button"
              class="inline-flex h-8 flex-none cursor-pointer items-center rounded-lg border-0 bg-[var(--dd-btn-secondary-fill)]
                     px-3 text-base font-medium leading-none text-[var(--dd-btn-secondary-label)]
                     transition-opacity hover:opacity-75 disabled:cursor-default disabled:opacity-60"
              [disabled]="disabled()"
              [attr.data-cy]="primaryActionDataCy()"
              (click)="onPrimaryAction()">
              {{ primaryActionLabel() }}
            </button>
          }
        </div>
        <ng-container [ngTemplateOutlet]="noticeCallout" />
      </div>
    } @else if (variant() === 'review') {
      <!--
        Beoordelingsvariant (Figma "Documents & uploads" 4082:159664, export 3 sep). Verticale
        ritmiek uit het ontwerp: labelregel → 8px → uploadregel (4082:159660 gap 8), en 12px
        naar de metaregels (4082:159664 gap 12). The reviewer still needs to see the current
        verdict — an approved-but-expired or previously-rejected document must not read the same
        as a fresh, untouched one — so the status dot + neutral label sit on the upload row below
        (#21239: colour lives in the 8px dot, the label text stays plain grey, no filled pill).
      -->
      <div class="flex flex-col gap-3" [attr.data-cy]="dataCy()">
        <div class="flex flex-col gap-2">
          <!-- Figma 4082:159661 (125×32, HORIZONTAL, gap 8, align CENTER): de vlagknop staat
               inline achter het label, niet rechts uitgelijnd op de volle breedte. -->
          <div class="flex flex-wrap items-center gap-2">
            <span
              class="text-[0.75rem] font-medium leading-[1.4] text-[color:var(--dd-accent)]"
              data-cy="document-card-label">
              {{ title() }}
              @if (required()) {
                <span aria-hidden="true"> *</span><span class="sr-only"> (verplicht)</span>
              }
            </span>
            <ng-content select="[documentCardLabelActions]" />
          </div>

          <!-- Figma master 575:25358 "Upload card": padding 8/12, radius 8, 1px Border/Default,
               fill Surface/Card; binnenin "File info" met gap 16. Altijd zichtbaar, ook zonder
               bestand — een documentrecord zonder bestand moet zijn menu (geschiedenis/archiveren)
               en zijn "ontbreekt nog"-status net zo goed tonen als een geüpload record. -->
          <div
            class="flex items-center gap-4 rounded-lg border border-[var(--dd-card-border)] bg-card px-3 py-2"
            data-cy="document-card-upload-row">
            <div class="flex min-w-0 flex-1 flex-col gap-1 py-1">
              @if (file(); as f) {
                <ng-container [ngTemplateOutlet]="fileNameLabel" [ngTemplateOutletContext]="{ $implicit: f }" />
              }
              <!-- Calm read status (#21239): the colour lives in the 8px dot, the label stays
                   neutral grey — no filled pill. A present file also shows its upload moment. -->
              <p
                class="m-0 flex flex-wrap items-center gap-1.5 text-[0.75rem] leading-normal
                       text-[var(--dd-card-muted-fg)]">
                <span
                  [class]="'inline-block h-1.5 w-1.5 flex-none rounded-full ' + reviewStatus().dotClass"
                  data-cy="document-card-status-dot"
                  aria-hidden="true"></span>
                <span data-cy="document-card-status">{{ reviewStatus().label }}</span>
                @if (file()) {
                  <span class="inline-block h-1 w-1 rounded-full bg-current opacity-40" aria-hidden="true"></span>
                  <span>{{ uploadedAtLabel() }}</span>
                }
              </p>
            </div>
            <div class="flex flex-none items-center gap-1">
              @if (file() && menu().preview) {
                <button
                  type="button"
                  [class]="ghostIconButtonClasses"
                  (click)="preview.emit()"
                  data-cy="document-preview-inline-button"
                  aria-label="Bekijk geüpload bestand">
                  <mat-icon class="icon-size-4" svgIcon="heroicons_outline:eye"></mat-icon>
                </button>
              }
              @if (file() && menu().download) {
                <button
                  type="button"
                  [class]="ghostIconButtonClasses"
                  (click)="download.emit()"
                  data-cy="document-download-inline-button"
                  aria-label="Download bestand">
                  <mat-icon class="icon-size-4" svgIcon="heroicons_outline:arrow-down-tray"></mat-icon>
                </button>
              }
              <!-- Alleen wat NIET al als icoonknop staat rechtvaardigt een overloopmenu. -->
              @if (hasOverflowMenu()) {
                <button
                  type="button"
                  [class]="ghostIconButtonClasses"
                  [matMenuTriggerFor]="cardMenu"
                  data-cy="document-actions-menu"
                  aria-label="Meer acties">
                  <mat-icon class="icon-size-4" svgIcon="heroicons_outline:ellipsis-horizontal"></mat-icon>
                </button>
              }
            </div>
          </div>

          <!-- The design has no rejection reason here, but a rejected document without its
               reason leaves the reviewer guessing why an earlier round failed. -->
          <ng-container [ngTemplateOutlet]="reviewCommentCallout" />

          <!-- Same notice a flagged document shows in the row variant — without it the reviewer
               computes a reason and never sees it rendered anywhere on this card. -->
          <ng-container [ngTemplateOutlet]="noticeCallout" />
        </div>

        @if (visibleMeta().length > 0) {
          <div class="grid grid-cols-1 gap-x-5 gap-y-3 sm:grid-cols-2">
            @for (row of visibleMeta(); track row.label) {
              <dd-label-list data-cy="document-card-meta" [label]="row.label" [value]="row.value"></dd-label-list>
            }
          </div>
        }
      </div>
    } @else {
      <div
        class="group flex h-full flex-col gap-5 rounded-lg border border-[var(--dd-card-border)] bg-card p-5
               transition-[border-color,box-shadow] duration-200
               hover:border-[var(--dd-card-border-strong)]
               hover:shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_-12px_rgba(15,23,42,0.10)]"
        [attr.data-cy]="dataCy()">
        <div class="flex items-start justify-between gap-3">
          <div class="flex flex-wrap items-center gap-1.5">
            @if (showBadge()) {
              <app-status-badge [label]="badgeLabel()" [variant]="badgeVariant()" [icon]="badgeIcon()"></app-status-badge>
            }
            @if (optional()) {
              <span class="text-[12px] leading-[1.4] text-accent">Optioneel</span>
            }
          </div>
          <!-- Een kaart zonder menu-items zou een leeg paneel openen; dan geen trigger. -->
          @if (hasMenu()) {
            <button
              type="button"
              class="-mr-2 -mt-2 inline-flex h-8 w-8 flex-none cursor-pointer items-center justify-center rounded-lg
                     border-0 bg-transparent text-secondary transition-colors
                     hover:bg-black/[0.04] dark:hover:bg-white/[0.06]"
              [matMenuTriggerFor]="cardMenu"
              data-cy="document-actions-menu"
              aria-label="Meer acties">
              <mat-icon class="icon-size-4" svgIcon="heroicons_outline:ellipsis-horizontal"></mat-icon>
            </button>
          }
        </div>

        <h4 class="m-0 line-clamp-2 break-words text-[0.9375rem] font-medium leading-snug text-default">
          {{ title() }}
          @if (required()) {
            <span aria-hidden="true"> *</span><span class="sr-only"> (verplicht)</span>
          }
        </h4>

        @if (description()) {
          <p class="m-0 text-[0.8125rem] leading-relaxed text-[var(--dd-card-muted-fg)]">{{ description() }}</p>
        }

        <ng-container [ngTemplateOutlet]="reviewCommentCallout" />

        <ng-container [ngTemplateOutlet]="noticeCallout" />

        @if (file(); as f) {
          <div class="flex items-start gap-4 rounded-md border border-[var(--dd-card-border)] p-2.5">
            <div class="flex min-w-0 flex-1 flex-col gap-1">
              <ng-container [ngTemplateOutlet]="fileNameLabel" [ngTemplateOutletContext]="{ $implicit: f }" />
              <p
                class="m-0 flex flex-wrap items-center gap-1.5 text-[0.75rem] leading-normal text-[var(--dd-card-muted-fg)]">
                <span>Geüpload {{ uploadedAtLabel() }}</span>
                @if (expiryLabel(); as expiry) {
                  <span class="inline-block h-1 w-1 rounded-full bg-current opacity-40" aria-hidden="true"></span>
                  <span>{{ expiry }}</span>
                }
              </p>
            </div>
            <button
              type="button"
              class="inline-flex h-8 w-8 flex-none cursor-pointer items-center justify-center rounded-lg border-0
                     bg-[var(--dd-badge-neutral-bg)] text-default transition-opacity hover:opacity-75"
              (click)="preview.emit()"
              data-cy="document-preview-inline-button"
              aria-label="Bekijk geüpload bestand">
              <mat-icon class="icon-size-4" svgIcon="heroicons_outline:eye"></mat-icon>
            </button>
          </div>
        }

        @if (busy()) {
          <div class="dd-file-row flex items-center gap-2 rounded-lg px-4 py-3 text-[0.8125rem] text-accent">
            <mat-spinner diameter="16"></mat-spinner>
            <span>Aan het uploaden…</span>
          </div>
        } @else if (uploadMode() === 'dropzone' && !file()) {
          <app-upload-dropzone
            [hint]="dropzoneHint()"
            [accept]="accept()"
            [disabled]="disabled()"
            [dropzoneLabel]="title() + ' uploaden'"
            (filesSelected)="filesSelected.emit($event)" />
        } @else if (primaryAction(); as action) {
          <div class="mt-auto flex items-center justify-end gap-3">
            <button
              type="button"
              class="inline-flex h-8 cursor-pointer items-center rounded-lg border-0 px-3 text-[0.875rem]
                     font-medium leading-none transition-opacity"
              [class]="action === 'upload' ? primaryButtonClasses : secondaryButtonClasses"
              [disabled]="disabled()"
              [attr.data-cy]="primaryActionDataCy()"
              (click)="onPrimaryAction()">
              {{ primaryActionLabel() }}
            </button>
          </div>
        }

        <!--
          Extension point for surface-specific actions the shared contract has no opinion on
          (e.g. the onboarding-review card's Goedkeuren/Afwijzen interface-action buttons). The
          card stays presentational — "the surface decides how an intent is handled" — so this
          projects host-supplied content rather than growing a new input/output pair per surface.
          An ng-content with no matching projected content renders nothing, so consumers that
          never project into this slot (documents-cards, the checklist) are pixel-unchanged.
        -->
        <ng-content select="[documentCardActions]" />
      </div>

    }

    <!--
      One rejection-reason markup for both the card and the review variant. A separate ng-template
      so the two branches cannot drift apart; an ng-container renders no element of its own, so the
      card keeps exactly the same DOM as before.
      Collapsed to two lines: a long rejection reason otherwise made its card much taller than its
      neighbours in the same grid. The full text sits behind the tooltip (hover/focus) AND behind a
      click — the latter because a tooltip does not open on touch, and the reason is precisely the
      most action-relevant thing on a rejected card.
    -->
    <!--
      One notice markup for both the card and the row variant. The row shows it ever since a
      reviewer can flag a document: the carrier's checklist runs in rows mode, so without this
      branch the reason WAS computed but shown nowhere — the carrier could only tell which document
      was wrong from the rejection text at the top. A separate ng-template so the two branches
      cannot drift apart; an ng-container renders no element of its own, so the card variant keeps
      exactly the same DOM as before.
    -->
    <ng-template #noticeCallout>
      @if (notice(); as text) {
        <p
          class="m-0 flex items-start gap-2 rounded bg-[var(--dd-badge-warning-bg)] px-3 py-2.5
                 text-[0.8125rem] leading-snug text-[var(--dd-badge-warning-fg)]"
          data-cy="document-card-notice">
          <mat-icon class="icon-size-4 mt-px flex-none" svgIcon="heroicons_solid:exclamation-triangle"></mat-icon>
          <span>{{ text }}</span>
        </p>
      }
    </ng-template>

    <ng-template #reviewCommentCallout>
      @if (reviewComment(); as comment) {
        <button
          type="button"
          class="m-0 flex w-full cursor-pointer items-start gap-2 rounded border-0
                 bg-[var(--dd-badge-negative-bg)] px-3 py-2.5 text-left text-[0.8125rem]
                 leading-snug text-[var(--dd-badge-negative-fg)]"
          [tippyTooltip]="comment"
          [tippyTooltipDisabled]="commentExpanded()"
          [attr.aria-expanded]="commentExpanded()"
          data-cy="document-card-review-comment"
          (click)="commentExpanded.set(!commentExpanded())">
          <mat-icon
            class="icon-size-4 mt-px flex-none"
            svgIcon="heroicons_outline:chat-bubble-left-ellipsis"></mat-icon>
          <span class="min-w-0 flex-1" [class.line-clamp-2]="!commentExpanded()">{{ comment }}</span>
        </button>
      }
    </ng-template>

    <!--
      One file-name markup for the card and the review variant. Both used to carry a literally
      duplicated 'document-card-file' paragraph, which could silently drift apart when the
      styling changed in one branch only.
    -->
    <ng-template #fileNameLabel let-file>
      <p
        class="m-0 truncate text-[0.875rem] font-medium leading-tight text-default"
        data-cy="document-card-file">
        {{ file.name || 'Bestand' }}
      </p>
    </ng-template>

    <!--
      One menu for every variant, outside the branches: a template reference declared inside an
      @if block is only visible within that block, and both the card and the review branch need a
      trigger for it. A mat-menu renders nothing until it is opened, so the card variant stays
      pixel-identical.
      Preview and download already sit in the review variant as icon buttons in the upload row;
      offering them again here would show the same thing twice.
    -->
    <mat-menu #cardMenu="matMenu">
      @if (menu().preview && !isReview()) {
        <button mat-menu-item type="button" data-cy="document-preview-button" (click)="preview.emit()">
          <mat-icon class="icon-size-4" svgIcon="heroicons_outline:eye"></mat-icon>
          <span>Bekijk bestand</span>
        </button>
      }
      @if (menu().download && !isReview()) {
        <button mat-menu-item type="button" data-cy="document-download-button" (click)="download.emit()">
          <mat-icon class="icon-size-4" svgIcon="heroicons_outline:arrow-down-tray"></mat-icon>
          <span>Downloaden</span>
        </button>
      }
      @if (menu().history) {
        <button mat-menu-item type="button" data-cy="document-history-button" (click)="history.emit()">
          <mat-icon class="icon-size-4" svgIcon="heroicons_outline:clock"></mat-icon>
          <span>Versiehistorie</span>
        </button>
      }
      @if (menu().archive) {
        <button mat-menu-item type="button" data-cy="document-archive-button" (click)="archive.emit()">
          <mat-icon class="icon-size-4" svgIcon="heroicons_outline:archive-box"></mat-icon>
          <span>Archiveren</span>
        </button>
      }
      @if (menu().replace) {
        <button mat-menu-item type="button" data-cy="document-menu-replace-button" (click)="upload.emit()">
          <mat-icon class="icon-size-4" svgIcon="heroicons_outline:arrow-path"></mat-icon>
          <span>Vervangen</span>
        </button>
      }
      @if (menu().complete) {
        <button mat-menu-item type="button" data-cy="document-complete-button" (click)="complete.emit()">
          <mat-icon class="icon-size-4" svgIcon="heroicons_outline:pencil-square"></mat-icon>
          <span>Gegevens aanvullen</span>
        </button>
      }
      @if (menu().example) {
        <button mat-menu-item type="button" data-cy="document-template-button" (click)="example.emit()">
          <mat-icon class="icon-size-4" svgIcon="heroicons_outline:arrow-down-tray"></mat-icon>
          <span>Voorbeeld downloaden</span>
        </button>
      }
    </mat-menu>
  `,
  host: { '[attr.data-variant]': 'variant()' },
  styles: `
    :host {
      display: block;
    }

    /* Alleen de kaartvariant is een gridtegel. De rij staat in een verticale lijst in een
       formulierkolom; daar zou height:100% hem oprekken en min-width hem laten overlopen. */
    :host([data-variant='card']) {
      height: 100%;
      /* Ondergrens, geen vaste breedte: de kaart volgt zijn container (gridkolom of
         formulierkolom), maar onder ~17rem wordt de bestandsregel met zijn oog-knop onleesbaar
         krap. Zonder deze grens kromp een kaart met korte teksten in een shrink-to-fit-context
         tot ~220px terwijl de buren 400px breed waren. */
      min-width: 17rem;
    }

    /* De globale outline-reset (fuse tailwind.scss) maakt custom buttons focus-onzichtbaar;
       repo-idioom is per-component compenseren met :focus-visible (WCAG 2.4.7). */
    button:focus-visible {
      outline: 2px solid var(--dd-focus-ring) !important;
      outline-offset: 2px;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentCardComponent {
  readonly title = input.required<string>();
  readonly description = input<string | null>(null);
  readonly showBadge = input(true);
  /** Combined review + validity verdict; label, colour and icon are derived from it. */
  readonly badge = input.required<DocumentCardBadge>();
  /** Rejection feedback from the reviewer, rendered as a callout above the file block. */
  readonly reviewComment = input<string | null>(null);
  /**
   * Waarschuwing die actie van de gebruiker vraagt zonder een afkeuring te zijn — bijvoorbeeld
   * verplichte gegevens die na het uploaden nog ontbreken.
   */
  readonly notice = input<string | null>(null);
  readonly file = input<DocumentCardFile | null>(null);
  readonly menu = input<DocumentCardMenu>({});
  /**
   * Label/value lines under the upload row — `review` variant only. Every other variant ignores
   * this input, so passing it never changes what `card` or `row` render.
   */
  readonly meta = input<DocumentCardMetaRow[]>([]);
  readonly primaryAction = input<DocumentCardPrimaryAction>(null);
  readonly uploadMode = input<DocumentCardUploadMode>('button');
  /**
   * Layout. `card` stays the default, so every consumer that does not ask for anything else
   * (documents-cards, the Bedrijven checklist, readonly-onboarding) renders exactly as before.
   */
  readonly variant = input<DocumentCardVariant>('card');
  /** Marks the document as mandatory — renders a `*` after the title. */
  readonly required = input(false);
  /** Renders an "Optioneel" marker beside the badge. */
  readonly optional = input(false);
  readonly disabled = input(false);
  /** Bestandsopslag loopt: sleepzone en knop wijken voor een voortgangsregel. */
  readonly busy = input(false);
  /** `accept` for the dropzone's file picker; ignored in button mode. */
  readonly accept = input('');
  readonly dropzoneHint = input('');
  readonly dataCy = input<string | null>(null);

  readonly preview = output<void>();
  readonly download = output<void>();
  readonly history = output<void>();
  readonly archive = output<void>();
  /** Every capture intent — upload, replace and retry all open the same runner. */
  readonly upload = output<void>();
  /** Download of the template's blank example file. */
  readonly example = output<void>();
  /** Reopen the create-form to fill in missing metadata, keeping the current file. */
  readonly complete = output<void>();
  /** Only in `dropzone` upload mode. */
  readonly filesSelected = output<FileList>();

  // Volledige literal class-strings: Tailwind's scanner leest bronbestanden als tekst en emit
  // alleen een utility die hij letterlijk ziet staan — een samengestelde string bereikt de
  // gecompileerde stylesheet nooit.
  protected readonly primaryButtonClasses = 'bg-black text-white dark:bg-white dark:text-black hover:opacity-90';
  protected readonly secondaryButtonClasses = 'bg-[var(--dd-badge-neutral-bg)] text-default hover:opacity-75';
  /** 32×32 ghost icon button (Figma "Icon buttons", Upload card): transparent until hover. */
  protected readonly ghostIconButtonClasses =
    'inline-flex h-8 w-8 flex-none cursor-pointer items-center justify-center rounded-lg border-0 bg-transparent text-secondary transition-colors hover:bg-black/[0.04] dark:hover:bg-white/[0.06]';

  /** Ingeklapte afkeurreden; klik wisselt naar de volledige tekst (touch-pad naast de tooltip). */
  protected readonly commentExpanded = signal(false);

  protected readonly badgeLabel = computed(() => documentBadgeLabel(this.badge()));
  protected readonly badgeVariant = computed(() => documentBadgeTone(this.badge()));
  protected readonly badgeIcon = computed(() => documentBadgeIcon(this.badge()));

  /**
   * The calm read-view status of the review variant (#21239): a coloured 8px dot plus a neutral
   * label, replacing the filled status pill. The dot carries the tone; the label stays "Geüpload"
   * for a present, in-order file (positive/neutral) and only switches to the attention wording
   * when the tone warrants it (verloopt, verlopen, afgekeurd, in behandeling). No file at all reads
   * as "Nog niet aangeleverd".
   */
  protected readonly reviewStatus = computed<{ dotClass: string; label: string }>(() => {
    if (!this.file()) {
      return { dotClass: STATUS_DOT_CLASSES.neutral, label: 'Nog niet aangeleverd' };
    }
    const tone = documentBadgeTone(this.badge());
    if (isDeviatingVariant(tone)) {
      return { dotClass: STATUS_DOT_CLASSES[tone], label: documentBadgeLabel(this.badge()) };
    }
    return { dotClass: STATUS_DOT_CLASSES.positive, label: 'Geüpload' };
  });

  protected readonly hasMenu = computed(() => Object.values(this.menu()).some(Boolean));

  protected readonly isReview = computed(() => this.variant() === 'review');

  /**
   * Overflow trigger of the review variant. Preview and download already sit in the upload row
   * as icon buttons there, so a menu carrying only those two would repeat what is on screen —
   * the trigger appears only once something else (history, archive, replace, …) needs a home.
   */
  protected readonly hasOverflowMenu = computed(() => {
    const { preview: _preview, download: _download, ...rest } = this.menu();
    return Object.values(rest).some(Boolean);
  });

  /**
   * Meta rows worth rendering. An absent value is dropped rather than shown as "—": the review
   * lists what the carrier actually supplied, and an empty string is as absent as a null here
   * (a cleared text field persists as '').
   */
  protected readonly visibleMeta = computed(() =>
    this.meta().filter(row => row.value !== null && row.value !== undefined && row.value !== '')
  );

  protected readonly primaryActionLabel = computed(() => {
    const action = this.primaryAction();
    return action ? PRIMARY_ACTION_LABELS[action] : '';
  });

  protected readonly primaryActionDataCy = computed(() => {
    const action = this.primaryAction();
    return action ? PRIMARY_ACTION_DATA_CY[action] : null;
  });

  /** `complete` is its own intent; every other primary action opens the same create-form runner. */
  protected onPrimaryAction(): void {
    if (this.primaryAction() === 'complete') this.complete.emit();
    else this.upload.emit();
  }

  protected readonly uploadedAtLabel = computed(() => formatShortDate(this.file()?.uploadedAt));

  /**
   * Second half of the meta line. Suppressed for expiring/expired documents: their verdict
   * already sits in the badge, and repeating it beside "Geüpload …" reads as two conflicting
   * statuses on one card — which also means this label never needs a warning/negative colour.
   */
  protected readonly expiryLabel = computed<string | null>(() => {
    const badge = this.badge();
    if (!badge.expiresAt) return null;
    if (badge.badge === 'expired' || badge.badge === 'expiring') return null;
    return `Geldig tot ${formatShortDate(badge.expiresAt)}`;
  });
}

/** dd-MM-yy, zoals de Figma-kaart ("Uploaded 25-01-26"). */
function formatShortDate(raw: Date | string | null | undefined): string {
  if (!raw) return '—';
  const date = raw instanceof Date ? raw : new Date(raw);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('nl-NL', { day: '2-digit', month: '2-digit', year: '2-digit' }).format(date);
}
